import { userAtom } from "@/atoms/userAtom"
import { ITeamMember } from "@/types/user.types"
import { capitalize } from "lodash"
import { formatPhoneNumber } from "@/utils/FormatPhoneNumber"
import { IconProp } from "@fortawesome/fontawesome-svg-core"
import { faEllipsis } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Dropdown, Table } from "antd"
import { useTranslation } from "react-i18next"
import { useRecoilValue } from "recoil"
import OrgUsersActionsMenu from "./OrgUsersActionsMenu"
import { convertTimestamp } from "@/utils/datetime"
import { isTeamMemberEditable } from "../helpers"
import { rolesKeys } from "@/constants/role"

interface OrgUsersTableProps {
  data: ITeamMember[]
  getOrgMembers: () => void
  isLoading: boolean
}

const OrgUsersTable: React.FC<OrgUsersTableProps> = ({ data, getOrgMembers, isLoading }) => {
  const user = useRecoilValue(userAtom)
  const { t } = useTranslation()
  const columns = [
    {
      title: t("common.name"),
      dataIndex: "name",
      key: "1",
      render: (_: any, record: ITeamMember) => {
        return record?.firstName || record?.lastName
          ? `${record.firstName} ${record.lastName}`
          : record?.email
      },
    },
    {
      title: t("profile.TeamPeopleTable.role"),
      dataIndex: "role",
      key: "2",
      render: (_: any, record: ITeamMember) => {
        const role = rolesKeys.find((role) => role.value === record.role)
        return role ? t(role?.title) : capitalize(record.role)
      },
    },
    {
      title: t("common.email"),
      dataIndex: "email",
      key: "3",
      render: (_: any, record: ITeamMember) => {
        return record.email
      },
    },
    {
      title: t("common.phoneNumber"),
      dataIndex: "phone",
      key: "4",
      render: (_: any, record: ITeamMember) => {
        return formatPhoneNumber(record.phone || "")
      },
    },
    {
      title: t("profile.TeamPeopleTable.lastActive"),
      dataIndex: "lastActive",
      key: "6",
      render: (_: any, record: ITeamMember) => {
        const lastActiveText = record.lastActive ? convertTimestamp(record.lastActive) : "-"
        return record.invitationLink ? t("profile.TeamPeopleTable.pending") : lastActiveText
      },
    },
    {
      title: t("profile.TeamPeopleTable.actions"),
      dataIndex: "setting",
      key: "7",
      render: (_: any, record: ITeamMember) => {
        const isEditable = isTeamMemberEditable(user, record)
        return (
          isEditable && (
            <Dropdown
              overlay={<OrgUsersActionsMenu getOrgMembers={getOrgMembers} member={record} />}
              trigger={["click"]}
            >
              <div className={"flex justify-center"}>
                <FontAwesomeIcon
                  className="flex-shrink-0 h-4 text-gray-500 text-sm cursor-pointer"
                  icon={faEllipsis as IconProp}
                />
              </div>
            </Dropdown>
          )
        )
      },
    },
  ]

  return (
    <Table
      size="small"
      rowKey={"email"}
      dataSource={data}
      columns={columns}
      pagination={{
        defaultPageSize: 12,
      }}
      rowClassName={(record: ITeamMember) => {
        return user.id === record.id ? "font-bold" : ""
      }}
      scroll={{ x: 600 }}
      loading={isLoading}
    />
  )
}

export default OrgUsersTable
